import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Copy, Download, Send, Ban, Pencil, AlertTriangle, CheckCircle2 } from "lucide-react";
import { API_BASE, formatApiError } from "./AuthContext";
import EngagementEditor from "./EngagementEditor";
import { fmtAUD } from "./EngagementSignView";

const STATUS_STYLE = {
  draft: { bg: "var(--subtle)", fg: "var(--ink-2)" }, sent: { bg: "var(--accent)", fg: "var(--paper)" },
  signed: { bg: "var(--moss)", fg: "var(--paper)" }, expired: { bg: "#8b8378", fg: "var(--paper)" },
  void: { bg: "transparent", fg: "var(--ink-3)" },
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "");

function EngagementRow({ e, onEdit, onChanged }) {
  const st = STATUS_STYLE[e.status] || STATUS_STYLE.draft;
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); onChanged(); } catch (err) { toast.error(formatApiError(err)); } };
  const send = () => act(async () => {
    const r = await axios.post(`${API_BASE}/engagements/${e.engagement_id}/send`, {});
    await navigator.clipboard?.writeText(r.data.link).catch(() => {});
  }, "Sent — signing link copied");
  const copyLink = () => act(async () => {
    const r = await axios.post(`${API_BASE}/engagements/${e.engagement_id}/send`, {});
    await navigator.clipboard.writeText(r.data.link);
  }, "Re-sent and signing link copied");
  const voidIt = () => window.confirm("Void this engagement letter?") && act(() => axios.post(`${API_BASE}/engagements/${e.engagement_id}/void`), "Voided");
  return (
    <li className="card p-3" data-testid={`engagement-row-${e.engagement_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[14px] font-medium">{e.financial_year} engagement letter</div>
          <div className="text-[11px] text-[var(--ink-3)] font-mono mt-0.5">
            {fmtAUD(e.total)} · {(e.items || []).length} service(s)
            {e.signed_at && ` · signed ${fmtDate(e.signed_at)} by ${e.signed_name} (${e.signed_via})`}
            {e.expires_at && ` · ${e.status === "expired" ? "expired" : "expires"} ${fmtDate(e.expires_at)}`}
            {e.renewal_of && " · renewal draft"}
          </div>
        </div>
        <span className="badge text-[10px]" style={{ background: st.bg, color: st.fg }} data-testid={`engagement-status-${e.engagement_id}`}>{e.status}</span>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2.5">
        {["draft", "sent"].includes(e.status) && <button onClick={() => onEdit(e)} className="btn-secondary !py-0.5 !px-2 text-[11px]" data-testid={`engagement-edit-${e.engagement_id}`}><Pencil size={11} /> Edit</button>}
        {e.status === "draft" && <button onClick={send} className="btn-primary !py-0.5 !px-2 text-[11px]" data-testid={`engagement-send-${e.engagement_id}`}><Send size={11} /> Send</button>}
        {e.status === "sent" && <button onClick={copyLink} className="btn-secondary !py-0.5 !px-2 text-[11px]" data-testid={`engagement-copy-link-${e.engagement_id}`}><Copy size={11} /> Resend & copy link</button>}
        <a href={`${API_BASE}/engagements/${e.engagement_id}/pdf`} className="btn-secondary !py-0.5 !px-2 text-[11px]" data-testid={`engagement-pdf-${e.engagement_id}`}><Download size={11} /> PDF</a>
        {["draft", "sent"].includes(e.status) && <button onClick={voidIt} className="btn-secondary !py-0.5 !px-2 text-[11px]" data-testid={`engagement-void-${e.engagement_id}`}><Ban size={11} /> Void</button>}
      </div>
    </li>
  );
}

export default function ClientEngagementsPanel({ clientId, onChanged }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);   // null | {} (new) | engagement
  const load = () => axios.get(`${API_BASE}/engagements`, { params: { client_id: clientId } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => { setEditing(null); load(); onChanged?.(); };

  const now = new Date().toISOString();
  const active = rows.find((r) => r.status === "signed" && (r.expires_at || "") > now);
  return (
    <div className="p-5 space-y-3" data-testid="client-engagements-panel">
      {active ? (
        <div className="text-[12px] inline-flex items-center gap-1.5 text-[var(--moss)]" data-testid="engagement-flag-ok"><CheckCircle2 size={13} /> Signed engagement in place until {fmtDate(active.expires_at)}</div>
      ) : (
        <div className="text-[12px] inline-flex items-center gap-1.5 text-[#B45309] bg-[#FEF3C7] rounded px-2 py-1" data-testid="engagement-flag-missing"><AlertTriangle size={13} /> No signed engagement letter for this client</div>
      )}
      {editing ? (
        <EngagementEditor clientId={clientId} initial={editing.engagement_id ? editing : null} onClose={() => setEditing(null)} onSaved={refresh} />
      ) : (
        <button onClick={() => setEditing({})} className="btn-secondary text-[12px]" data-testid="engagement-new-btn"><Plus size={12} /> New engagement letter</button>
      )}
      <ul className="space-y-2">
        {rows.map((e) => <EngagementRow key={e.engagement_id} e={e} onEdit={setEditing} onChanged={refresh} />)}
      </ul>
    </div>
  );
}
